/**
 * The platform console's Gateways section, its pure part (docs/plans/2026-10-18_remote-gateway-console.md, P0): the
 * filters kept in the page URL (so a link opens the same view), the searchable organisation filter, and the words the
 * list and the details show. The view is routes/platform/gateways-console.tsx.
 */

import { formatDistance } from "date-fns";
import type { AdminGatewayDetail, AdminGatewayQuery, AdminGatewayRow, AdminGatewayUplink } from "@/lib/api/admin-gateways";
import { opLabel } from "@/lib/iot/gateway-link";

/** The detail tabs, in the order the app's screens follow. */
export const DETAIL_TABS = ["overview", "network", "devices", "diagnostics", "firmware"] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

/** The console's view, as the URL holds it. */
export interface GatewayConsoleState {
  /** The orgs the list is narrowed to; none: every org. */
  orgs: string[];
  /** The sites, inside the chosen orgs. */
  sites: string[];
  /** A name, serial or id. */
  q: string;
  online: "" | "online" | "offline";
  /** The firmware line (sgwp, sgwmp, sgwe). */
  line: string;
  /** The gateway open on the right. */
  gw: string | null;
  view: DetailTab;
}

/** The URL parameters the section owns; the console's own `tab` is not one of them. */
export const CONSOLE_PARAMS = ["org", "site", "q", "online", "line", "gw", "view"] as const;

/** A parameter that may repeat and may hold commas. */
function many(p: URLSearchParams, name: string): string[] {
  const out: string[] = [];
  for (const v of p.getAll(name)) {
    for (const s of v.split(",")) {
      const t = s.trim();
      if (t && !out.includes(t)) out.push(t);
    }
  }
  return out;
}

/** The view a URL holds; anything unreadable falls back to its default. */
export function readConsoleState(p: URLSearchParams): GatewayConsoleState {
  const online = p.get("online");
  const view = p.get("view");
  return {
    orgs: many(p, "org"),
    sites: many(p, "site"),
    // As typed: the search box shows it, and the server query trims it.
    q: p.get("q") ?? "",
    online: online === "online" || online === "offline" ? online : "",
    line: (p.get("line") ?? "").trim().toLowerCase(),
    gw: p.get("gw") || null,
    view: (DETAIL_TABS as readonly string[]).includes(view ?? "") ? (view as DetailTab) : "overview",
  };
}

/**
 * The URL for a view: the section's parameters replaced, every other one (the console's `tab`) kept. Defaults are
 * left out, so a plain view has a plain URL.
 */
export function writeConsoleState(base: URLSearchParams, s: GatewayConsoleState): URLSearchParams {
  const p = new URLSearchParams(base);
  for (const k of CONSOLE_PARAMS) p.delete(k);
  if (s.orgs.length > 0) p.set("org", s.orgs.join(","));
  if (s.sites.length > 0) p.set("site", s.sites.join(","));
  if (s.q.trim()) p.set("q", s.q);
  if (s.online) p.set("online", s.online);
  if (s.line) p.set("line", s.line);
  if (s.gw) p.set("gw", s.gw);
  if (s.gw && s.view !== "overview") p.set("view", s.view);
  return p;
}

/** The list's server query for a view. */
export function listQuery(s: GatewayConsoleState): AdminGatewayQuery {
  return {
    orgIds: s.orgs,
    siteIds: s.sites,
    q: s.q.trim(),
    online: s.online === "" ? null : s.online === "online",
    line: s.line,
    limit: 50,
  };
}

/** An org as the organisation filter offers it. */
export interface OrgOption {
  id: string;
  name: string;
  slug: string;
}

/**
 * The orgs the organisation filter offers for what was typed: part of a name or slug, any case; the chosen ones
 * first. An empty query offers every org.
 */
export function matchOrgs(orgs: OrgOption[], query: string, chosen: string[] = []): OrgOption[] {
  const q = query.trim().toLowerCase();
  const hit = orgs.filter((o) => !q || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q));
  return [...hit].sort((a, b) => {
    const ca = chosen.includes(a.id) ? 0 : 1;
    const cb = chosen.includes(b.id) ? 0 : 1;
    return ca - cb || a.name.localeCompare(b.name);
  });
}

/** An org chosen or not chosen. */
export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** A site the site filter offers: one with gateways in the chosen orgs. */
export interface SiteOption {
  id: string;
  name: string;
  orgName: string;
}

/** The sites of these gateways, once each, by org then name. */
export function sitesOf(rows: Pick<AdminGatewayRow, "site_id" | "site_name" | "org_name">[]): SiteOption[] {
  const seen = new Map<string, SiteOption>();
  for (const r of rows) {
    if (!seen.has(r.site_id)) seen.set(r.site_id, { id: r.site_id, name: r.site_name, orgName: r.org_name });
  }
  return [...seen.values()].sort((a, b) => a.orgName.localeCompare(b.orgName) || a.name.localeCompare(b.name));
}

/** The firmware lines (ADR 0002 A18): the line's code and the hardware model, as the app names them. */
const LINES: Record<string, { code: string; model: string }> = {
  sgwp: { code: "SGWP", model: "SBC Gateway" },
  sgwmp: { code: "SGWMP", model: "SBC Gateway with screen" },
  sgwe: { code: "SGWE", model: "IoT Bridge" },
};

/** The firmware line's name; "No line" when the birth names none (older firmware). */
export function lineLabel(line: string | null | undefined): string {
  if (!line) return "No line";
  const l = LINES[line.toLowerCase()];
  return l ? `${l.code} · ${l.model}` : line.toUpperCase();
}

/**
 * The line as the list's model chip shows it: its key ("sgwp", for the chip's colour), code and model. A line the
 * console does not know keeps its code and has no model; no line at all is null.
 */
export function lineModel(line: string | null | undefined): { key: string; code: string; model: string | null } | null {
  if (!line) return null;
  const key = line.toLowerCase();
  const l = LINES[key];
  return l ? { key, ...l } : { key, code: line.toUpperCase(), model: null };
}

/** A version split where its pre-release or build part starts: "0.1.3-dev.2655a24" is ["0.1.3", "-dev.2655a24"]. */
export function splitVersion(v: string): [string, string] {
  const i = v.search(/[-+]/);
  return i > 0 ? [v.slice(0, i), v.slice(i)] : [v, ""];
}

/** The org and site that every row shares, so the list can name it once; null when the rows differ or are none. */
export function sharedPlace(
  rows: Pick<AdminGatewayRow, "org_id" | "org_name" | "site_id" | "site_name">[],
): { orgName: string; siteName: string } | null {
  const first = rows[0];
  if (!first || rows.some((r) => r.org_id !== first.org_id || r.site_id !== first.site_id)) return null;
  return { orgName: first.org_name, siteName: first.site_name };
}

/** The lines the line filter offers. */
export const LINE_OPTIONS = Object.keys(LINES);

/** The uplink in words: what carries the gateway's internet. */
export function uplinkWords(u: AdminGatewayUplink | null | undefined): string {
  if (!u) return "No uplink reported";
  const ssid = u.kind === "wifi" && u.ssid ? ` (${u.ssid})` : "";
  return `${uplinkKindWord(u)} ${u.iface}${ssid}`;
}

/** The uplink's kind in a word: "Wi-Fi", "Cable", or the kind the gateway sent. */
export function uplinkKindWord(u: Pick<AdminGatewayUplink, "kind">): string {
  return u.kind === "wifi" ? "Wi-Fi" : u.kind === "ethernet" ? "Cable" : u.kind || "Link";
}

/** "3 minutes ago", or "never". */
export function agoWords(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "never";
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return "never";
  return formatDistance(t, now, { addSuffix: true });
}

/** The list's short form of agoWords: "just now", "4 min ago", "3 h ago", "2 d ago", or "never". */
export function shortAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "never";
  const min = Math.floor((now.getTime() - t) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  if (min < 48 * 60) return `${Math.floor(min / 60)} h ago`;
  return `${Math.floor(min / (24 * 60))} d ago`;
}

/** Online, offline, or not yet claimed: the list's dot. */
export function presenceOf(g: Pick<AdminGatewayRow, "online" | "claimed">): "online" | "offline" | "unclaimed" {
  if (!g.claimed) return "unclaimed";
  return g.online ? "online" : "offline";
}

/**
 * Whether a live read can run now, and why not: the gateway must be claimed and online, its firmware must take remote
 * requests, and its remote_ops must list the opcode (the cloud checks all of this again, and the gateway decides).
 */
export function opGate(
  detail: Pick<AdminGatewayDetail, "online" | "claimed" | "remote_ops">,
  allowed: Set<number>,
  opcode: number,
): { ok: boolean; why?: string } {
  if (!detail.claimed) return { ok: false, why: "The gateway is not claimed yet." };
  if (!detail.online) return { ok: false, why: "The gateway is offline." };
  if (!detail.remote_ops) return { ok: false, why: "This gateway's firmware takes no remote requests." };
  if (!allowed.has(opcode)) return { ok: false, why: `This gateway does not take ${opLabel(opcode)} remotely.` };
  return { ok: true };
}
